import PropTypes from 'prop-types';
import {
  Drawer, Text, Badge, Stack, Group, Code, Button,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useNavigate } from 'react-router-dom';
import ReassignAgentModal from '@/components/ReassignAgentModal';
import { routeWithParams } from '@/helpers/route';
import routes from '@/routes';
import prepareTranslate from '@/helpers/dictionary';
import intakeEventMutations from '@/api/mutitations/intakeEvent';
import ActionSection from './ActionSection';
import classes from './style.module.css';

const EVENT_TYPE_COLOR = {
  filename_problem: 'orange',
  sheet_name_problem: 'yellow',
  import_problem: 'red',
  new_agent_created: 'blue',
};

const parseDetails = (details) => {
  if (!details) return null;
  try {
    const parsed = JSON.parse(details);
    // A failure that only says what went wrong is shown as the message itself
    const keys = Object.keys(parsed || {});
    if (typeof parsed?.error === 'string' && keys.every((key) => ['filename', 'error'].includes(key))) {
      return parsed.error;
    }
    return JSON.stringify(parsed, null, 2);
  } catch {
    return details;
  }
};

const extractAgentData = (event) => {
  let agentNumber = null;
  let identifierNumber = event.relatedEntity?.identifierNumber || null;
  let fullName = null;
  let alternativeFundNumbers = [];
  let clientNumbers = [];
  let agreementNumbers = [];

  if (event.details) {
    try {
      const parsed = JSON.parse(event.details);
      agentNumber = parsed.agentNumber || null;
      identifierNumber = identifierNumber || parsed.identifierNumber || null;
      fullName = parsed.fullName || null;
      alternativeFundNumbers = parsed.alternativeFundNumbers || [];
      clientNumbers = parsed.clientNumbers || [];
      agreementNumbers = parsed.agreementNumbers || [];
    } catch {
      // old format: plain string — identifierNumber already set from relatedEntity
    }
  }

  return {
    agentNumber,
    identifierNumber,
    fullName,
    alternativeFundNumbers,
    clientNumbers,
    agreementNumbers,
  };
};

const EventDrawer = ({
  event, opened, onClose, onSuccess,
}) => {
  const t = prepareTranslate();
  const navigate = useNavigate();
  const [reassignOpened, { open: openReassign, close: closeReassign }] = useDisclosure(false);
  const {
    mutateAsync: updateEvent, isPending: isClosing,
  } = intakeEventMutations.useUpdateIntakeEvent();

  if (!event) return null;

  const handleReassignSuccess = () => {
    closeReassign();
    onSuccess();
  };

  const handleClose = async () => {
    try {
      await updateEvent({ id: event._id, status: 'closed' });
      onSuccess();
    } catch {
      // silent — event close failure is non-critical
    }
  };

  const formattedDetails = parseDetails(event.details);
  const {
    identifierNumber: agentIdentifier, fullName: agentFullName,
    alternativeFundNumbers, clientNumbers, agreementNumbers,
  } = event.eventType === 'new_agent_created'
    ? extractAgentData(event)
    : {
      identifierNumber: null,
      fullName: null,
      alternativeFundNumbers: [],
      clientNumbers: [],
      agreementNumbers: [],
    };

  return (
    <>
      <Drawer
        opened={opened}
        onClose={onClose}
        title={event.fileRef?.originalName || t('components.eventDrawer.noFilename')}
        position="right"
        size="md"
      >
        <Stack gap="md">
          <Group gap="xs">
            <Badge color={EVENT_TYPE_COLOR[event.eventType] || 'gray'}>
              {t(`pages.events.eventTypes.${event.eventType}`)}
            </Badge>
            {event.eventType !== 'new_agent_created' && (event.errorCodes?.length > 0 ? event.errorCodes : [event.errorCode].filter(Boolean)).map((code) => (
              <Badge key={code} variant="outline" color="gray">
                {t(`pages.events.errorCodes.${code}`) || code}
              </Badge>
            ))}
          </Group>

          <Text size="sm">
            <strong>{t('components.eventDrawer.labels.date')}</strong>{' '}
            {new Date(event.createdAt).toLocaleString('he-IL')}
          </Text>
          <Text size="sm"><strong>{t('components.eventDrawer.labels.user')}</strong> {event.userName}</Text>
          {event.fileRef?.commissionFileType && (
            <Text size="sm">
              <strong>{t('components.eventDrawer.labels.fileType')}</strong> {t(`components.eventDrawer.fileTypes.${event.fileRef.commissionFileType}`) || event.fileRef.commissionFileType}
            </Text>
          )}

          {formattedDetails && event.eventType !== 'new_agent_created' && (
            <Code block className={classes.codeBlock}>
              {formattedDetails}
            </Code>
          )}

          {agentFullName && (
            <Text size="sm">
              <strong>{t('components.eventDrawer.labels.agentFullName')}</strong>{' '}
              {agentFullName}
            </Text>
          )}

          {agentIdentifier && (
            <Text size="sm">
              <strong>{t('components.eventDrawer.labels.agentIdentifier')}</strong>{' '}
              {agentIdentifier}
            </Text>
          )}

          {alternativeFundNumbers.length > 0 && (
            <Text size="sm">
              <strong>{t('components.eventDrawer.labels.alternativeFundNumbers')}</strong>{' '}
              {alternativeFundNumbers.join(', ')}
            </Text>
          )}

          {clientNumbers.length > 0 && (
            <Text size="sm">
              <strong>{t('components.eventDrawer.labels.clientNumbers')}</strong>{' '}
              {clientNumbers.join(', ')}
            </Text>
          )}

          {agreementNumbers.length > 0 && (
            <Text size="sm">
              <strong>{t('components.eventDrawer.labels.agreementNumbers')}</strong>{' '}
              {agreementNumbers.join(', ')}
            </Text>
          )}

          {event.eventType === 'new_agent_created' && event.status !== 'closed' && (
            <Group gap="sm" mt="md">
              <Button
                onClick={() => navigate(
                  routeWithParams(routes.agentView.path, {
                    id: String(event.relatedEntity?.entityId),
                    tab: 'details',
                    state: 'edit',
                  }),
                )}
              >
                {t('components.eventDrawer.actions.editAgent')}
              </Button>
              <Button variant="light" onClick={openReassign}>
                {t('components.eventDrawer.actions.reassignAgent')}
              </Button>
              <Button
                variant="light"
                color="red"
                onClick={handleClose}
                loading={isClosing}
              >
                {t('components.eventDrawer.actions.closeEvent')}
              </Button>
            </Group>
          )}

          {event.eventType !== 'new_agent_created' && event.status !== 'closed' && (
            <ActionSection event={event} onSuccess={onSuccess} />
          )}
        </Stack>
      </Drawer>

      {event.eventType === 'new_agent_created' && event.status !== 'closed' && (
        <ReassignAgentModal
          opened={reassignOpened}
          onClose={closeReassign}
          event={event}
          onSuccess={handleReassignSuccess}
        />
      )}
    </>
  );
};

EventDrawer.propTypes = {
  event: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    eventType: PropTypes.string.isRequired,
    errorCode: PropTypes.string,
    errorCodes: PropTypes.arrayOf(PropTypes.string),
    details: PropTypes.string,
    userName: PropTypes.string,
    createdAt: PropTypes.string,
    fileRef: PropTypes.shape({
      originalName: PropTypes.string,
      commissionFileType: PropTypes.string,
    }),
    relatedEntity: PropTypes.shape({
      entityId: PropTypes.string,
      identifierNumber: PropTypes.string,
    }),
    status: PropTypes.string,
  }),
  opened: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

EventDrawer.defaultProps = {
  event: null,
};

export default EventDrawer;
